import React, { useState, useCallback, useEffect, useRef } from 'react'; // Add useRef
import { ChevronDown, ChevronUp, PoundSterlingIcon, Globe, Monitor, Zap } from 'lucide-react';
import CurrentMobileCosts from './CurrentMobileCosts';
import CostComparisonComponent from './CostComparisonComponent';
import RentalProfiling from './RentalProfiling';
import MobileService from '../services/mobileService';
import { fetchCommission } from "../services/mechanicsService";

const LICENCE_SERVICES = [
  {
    key: 'Wizzyl',
    label: 'Wizzyl',
    description: 'Website Platform',
    monthlyPrice: 49.99,
    fibreShare: 20.00,
    tntmShare: 29.99,
  },
  {
    key: '6ELA',
    label: '6ELA',
    description: 'Invoice & Credit Control Platform',
    monthlyPrice: 49.99,
    fibreShare: 20.00,
    tntmShare: 29.99,
  },
];

const AXO_PACKAGES = [
  { key: 'launch',    label: 'AXO Launch — 1,000 companies/mo',       price: 250,  fibre: 100,  tntm: 150,  companiesPerMonth: 1000 },
  { key: 'momentum',  label: 'AXO Momentum — 2,500 companies/mo',     price: 500,  fibre: 200,  tntm: 300,  companiesPerMonth: 2500 },
  { key: 'scale',     label: 'AXO Scale — 5,000 companies/mo',        price: 900,  fibre: 360,  tntm: 540,  companiesPerMonth: 5000 },
  { key: 'unlimited', label: 'AXO Unlimited — Unlimited lead data',   price: 7500, fibre: 3000, tntm: 4500, companiesPerMonth: null },
];

const CallCostsComponent = ({
  isHostedLifetimeLicenses,
  selectedConnectionId,
  totalMonthlyCost,
  itServicesTotal,
  leasedLines,
  totalEquipmentCost,
  onServiceChargesUpdate,
  onLeaseDataUpdate,
  serviceChargesData,
  leaseCalculatorData,
  savedDigitalServices,
  hardwareItems = { hardwareItems },
  additionalHardware = { additionalHardware },
  currentLines = { currentLines },
  dataBroadband = { dataBroadband },
  lineRequirements = { lineRequirements },
  telecomServices = { telecomServices },
  adminUpdate = { adminUpdate },
  onDigitalServicesUpdate,
}) => {
  const [isServiceChargesOpen, setIsServiceChargesOpen] = useState(false);
  const [isCurrentMobileCostsOpen, setisCurrentMobileCostsOpen] = useState(false);
  const [isWebHostingOpen, setIsWebHostingOpen] = useState(false);

  const [commission, setCommission] = useState(null);
  const [mobileData, setMobileData] = useState(null);

  const [localServiceCharges, setLocalServiceCharges] = useState({
    service_charge: 0,
    equipment_rentals: 0,
    broadband: 0,
    media: 0,
    maintenance: 0,
    utilities: 0,
  });

  const [webHostingCost, setWebHostingCost] = useState(0);
  const [licenceQtys, setLicenceQtys] = useState({ Wizzyl: 0, '6ELA': 0 });
  const [axoPackage, setAxoPackage] = useState('');

  // Add refs to track initialization and prevent loops
  const isInitialMount = useRef(true);
  const isHydratingFromSaved = useRef(false);
  const prevSavedDigitalServicesRef = useRef();

  const selectedAxo = AXO_PACKAGES.find(p => p.key === axoPackage) ?? null;

  const licenceMonthlyCost = LICENCE_SERVICES.reduce(
    (sum, svc) => sum + (parseInt(licenceQtys[svc.key]) || 0) * svc.monthlyPrice, 0
  );
  const totalNewDigitalCost = licenceMonthlyCost + (selectedAxo?.price || 0);

  const licenceFibreRevenue = LICENCE_SERVICES.reduce(
    (sum, svc) => sum + (parseInt(licenceQtys[svc.key]) || 0) * svc.fibreShare, 0
  );
  const totalFibreRevenue = licenceFibreRevenue + (selectedAxo?.fibre || 0);

  // ── Effect 1: Reset when connection changes ──
  useEffect(() => {
    setWebHostingCost(0);
    setLicenceQtys({ Wizzyl: 0, '6ELA': 0 });
    setAxoPackage('');
    
    // Reset refs when connection changes
    isInitialMount.current = true;
    isHydratingFromSaved.current = false;
    prevSavedDigitalServicesRef.current = null;
  }, [selectedConnectionId]);

  // ── Effect 2: Hydrate from savedDigitalServices ──
  useEffect(() => {
    // Skip if no saved data or if it's the same as previous
    if (!savedDigitalServices || 
        JSON.stringify(savedDigitalServices) === JSON.stringify(prevSavedDigitalServicesRef.current)) {
      return;
    }

    // Set hydrating flag to true
    isHydratingFromSaved.current = true;
    
    // Store current saved data for comparison
    prevSavedDigitalServicesRef.current = savedDigitalServices;

    if (savedDigitalServices.webHostingCost !== undefined) {
      setWebHostingCost(parseFloat(savedDigitalServices.webHostingCost) || 0);
    }

    if (savedDigitalServices.licenceServices && typeof savedDigitalServices.licenceServices === 'object') {
      setLicenceQtys(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(savedDigitalServices.licenceServices).map(([k, v]) => [k, parseInt(v) || 0])
        ),
      }));
    }

    if (savedDigitalServices.axoSignalPackage) {
      const savedKey =
        typeof savedDigitalServices.axoSignalPackage === 'string'
          ? savedDigitalServices.axoSignalPackage
          : savedDigitalServices.axoSignalPackage?.key ?? '';
      const match = AXO_PACKAGES.find(p => p.key === savedKey);
      if (match) setAxoPackage(match.key);
    }

    // Reset hydrating flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isHydratingFromSaved.current = false;
    }, 100);
  }, [savedDigitalServices]);

  useEffect(() => {
    const getMobileDetails = async () => {
      try {
        const response = await MobileService.getMobileService(selectedConnectionId);
        setMobileData(response);
      } catch (error) {
        console.error("Failed to fetch:", error);
      }
    };
    if (selectedConnectionId) getMobileDetails();
  }, [selectedConnectionId]);

  useEffect(() => {
    const loadCommission = async () => {
      try {
        const data = await fetchCommission();
        if (data?.commission !== undefined) setCommission(data.commission);
      } catch (err) {
        console.error("Error fetching commission:", err);
      }
    };
    loadCommission();
  }, []);

  useEffect(() => {
    if (serviceChargesData) {
      setLocalServiceCharges(prev => ({ ...prev, ...serviceChargesData }));
    }
  }, [serviceChargesData]);

  // Notify parent whenever digital services change (but skip during hydration)
  useEffect(() => {
    // Skip notification during initial mount or during hydration from saved data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Skip if we're currently hydrating from saved data
    if (isHydratingFromSaved.current) {
      return;
    }

    onDigitalServicesUpdate?.({
      webHostingCost,
      licenceServices: licenceQtys,
      axoSignalPackage: selectedAxo ?? null,
      totalNewDigitalCost,
      totalFibreDigitalRevenue: totalFibreRevenue,
    });
  }, [webHostingCost, licenceQtys, axoPackage, totalNewDigitalCost, totalFibreRevenue, selectedAxo]);

  const getDisplayValue = (value) => (value === 0 ? '' : value);
  const handleInputFocus = (e) => { if (e.target.value === '0') e.target.value = ''; };
  const handleInputBlur = (e, field) => {
    if (e.target.value === '' || e.target.value === null) handleServiceChargeChange(field, 0);
  };

  const handleServiceChargeChange = useCallback(
    (field, value) => {
      const newValue = parseFloat(value) || 0;
      const updatedCharges = { ...localServiceCharges, [field]: newValue };
      setLocalServiceCharges(updatedCharges);
      onServiceChargesUpdate?.(updatedCharges);
    },
    [localServiceCharges, onServiceChargesUpdate]
  );

  const handleLicenceQtyChange = (key, value) => {
    const parsed = Math.max(0, parseInt(value) || 0);
    setLicenceQtys(prev => ({ ...prev, [key]: parsed }));
  };

  const digitalBannerText =
    webHostingCost > 0 || totalNewDigitalCost > 0
      ? `Current hosting: £${parseFloat(webHostingCost || 0).toFixed(2)} | New services: £${totalNewDigitalCost.toFixed(2)}/mo`
      : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-6xl mx-auto space-y-4">

      {/* ── SERVICE CHARGE COSTS ── */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-200">
        <div
          className="bg-gradient-to-r from-blue-800 via-blue-900 to-black text-white p-6 cursor-pointer flex justify-between items-center hover:from-gray-700 hover:via-gray-800 hover:to-gray-900 transition-all duration-300"
          onClick={() => setIsServiceChargesOpen(v => !v)}
        >
          <div className="flex items-center space-x-3">
            <PoundSterlingIcon className="w-6 h-6" />
            <h2 className="text-xl font-bold tracking-wide">CURRENT SERVICE CHARGE COSTS</h2>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-300 text-sm font-medium">{isServiceChargesOpen ? 'Collapse' : 'Expand'}</span>
            <div className="bg-gray-700 rounded-full p-1">
              {isServiceChargesOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </div>
        <div className={isServiceChargesOpen ? 'block' : 'hidden'}>
          <div className="bg-gray-50 p-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Service Charge Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { field: 'service_charge',    label: 'Existing Service Charges' },
                  { field: 'equipment_rentals', label: 'Existing Equipment Rentals' },
                  { field: 'media',             label: 'PH Media / On Hold Marketing' },
                  { field: 'maintenance',       label: 'Existing Maintenance' },
                  { field: 'broadband',         label: 'Existing Broadband Charges' },
                  { field: 'utilities',         label: 'Any Other Costs' },
                ].map(({ field, label }) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                    <input type="number" step="0.01"
                      value={getDisplayValue(localServiceCharges[field])}
                      onChange={(e) => handleServiceChargeChange(field, e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={(e) => handleInputBlur(e, field)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-lg font-bold text-gray-800">Total Service Charges:</span>
                <span className="text-xl font-bold text-blue-600">
                  £{Object.values(localServiceCharges).reduce((a, b) => a + b, 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── WEBSITE HOSTING & DIGITAL SERVICES ── */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-200">
        <div
          className="bg-gradient-to-r from-blue-800 via-blue-900 to-black text-white p-6 cursor-pointer flex justify-between items-center hover:from-gray-700 hover:via-gray-800 hover:to-gray-900 transition-all duration-300"
          onClick={() => setIsWebHostingOpen(v => !v)}
        >
          <div className="flex items-center space-x-3">
            <Globe className="w-6 h-6" />
            <h2 className="text-xl font-bold tracking-wide">WEBSITE HOSTING & DIGITAL SERVICES</h2>
          </div>
          <div className="flex items-center space-x-2">
            {!isWebHostingOpen && digitalBannerText && (
              <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {digitalBannerText}
              </span>
            )}
            <span className="text-gray-300 text-sm font-medium">{isWebHostingOpen ? 'Collapse' : 'Expand'}</span>
            <div className="bg-gray-700 rounded-full p-1">
              {isWebHostingOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </div>

        <div className={isWebHostingOpen ? 'block' : 'hidden'}>
          <div className="bg-gray-50 p-6 space-y-6">

            {/* Current website hosting cost */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-600" /> Current Spent
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Enter the customer's current spend, including costs related to website infrastructure, credit control, and lead generation
              </p>
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Hosting Cost (£)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
                  <input
                    type="number" step="0.01" min="0"
                    value={webHostingCost === 0 ? '' : webHostingCost}
                    onChange={(e) => setWebHostingCost(parseFloat(e.target.value) || 0)}
                    onFocus={(e) => { if (e.target.value === '0') e.target.value = ''; }}
                    onBlur={(e)  => { if (e.target.value === '') setWebHostingCost(0); }}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Wizzyl & 6ELA */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" /> Wizzyl &amp; 6ELA — Licence Subscriptions
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                Each licence is <span className="font-semibold text-blue-700">£49.99/month</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {LICENCE_SERVICES.map((svc) => {
                  const qty     = licenceQtys[svc.key] ?? 0;
                  const subtotal = qty * svc.monthlyPrice;
                  return (
                    <div key={svc.key} className={`rounded-xl border-2 p-5 transition-all duration-200 ${
                      qty > 0 ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                    }`}>
                      <div className="mb-3">
                        <span className="inline-block bg-gradient-to-r from-blue-800 to-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full mb-1">
                          {svc.label}
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">{svc.description}</p>
                        <p className="text-xs font-semibold text-blue-700 mt-1">£{svc.monthlyPrice.toFixed(2)} / licence / month</p>
                      </div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">No. of Licences</label>
                      <div className="flex items-center gap-2">
                        <button type="button"
                          onClick={() => handleLicenceQtyChange(svc.key, qty - 1)}
                          disabled={qty <= 0}
                          className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-blue-200 text-gray-700 font-bold text-lg flex items-center justify-center transition-colors disabled:opacity-40"
                        >−</button>
                        <input type="number" min="0"
                          value={qty === 0 ? '' : qty}
                          onChange={(e) => handleLicenceQtyChange(svc.key, e.target.value)}
                          onFocus={(e) => { if (e.target.value === '0') e.target.value = ''; }}
                          onBlur={(e)  => { if (e.target.value === '') handleLicenceQtyChange(svc.key, 0); }}
                          className="w-16 text-center px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold"
                          placeholder="0"
                        />
                        <button type="button"
                          onClick={() => handleLicenceQtyChange(svc.key, qty + 1)}
                          className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-blue-200 text-gray-700 font-bold text-lg flex items-center justify-center transition-colors"
                        >+</button>
                      </div>
                      {qty > 0 && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Customer pays:</span>
                            <span className="font-semibold text-gray-700">£{subtotal.toFixed(2)}/mo</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* AXO Signal */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" /> AXO Signal — Market Intelligence
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                AXO Signal is a standalone subscription. Select the package that best fits the customer's needs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {AXO_PACKAGES.map((pkg) => {
                  const isSelected = axoPackage === pkg.key;
                  return (
                    <button key={pkg.key} type="button"
                      onClick={() => setAxoPackage(isSelected ? '' : pkg.key)}
                      className={`text-left rounded-xl border-2 p-4 transition-all duration-200 ${
                        isSelected ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-300' : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                      }`}
                    >
                      <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full mb-2 ${
                        isSelected ? 'bg-blue-600 text-white' : 'bg-gray-700 text-white'
                      }`}>
                        {pkg.key.charAt(0).toUpperCase() + pkg.key.slice(1)}
                      </span>
                      <p className="text-xs text-gray-500 mb-2 leading-snug">{pkg.label.split('—')[1]?.trim()}</p>
                      <p className="text-base font-bold text-gray-800">
                        £{pkg.price.toFixed(2)}<span className="text-xs font-normal text-gray-500">/mo</span>
                      </p>
                    </button>
                  );
                })}
              </div>
              {selectedAxo && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Selected package</p>
                    <p className="text-sm font-bold text-blue-900">{selectedAxo.label}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Customer pays</p>
                    <p className="text-sm font-bold text-gray-800">£{selectedAxo.price.toFixed(2)}/mo</p>
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <span className="text-xs text-gray-500 mb-1">Current Hosting Cost</span>
                <span className="text-lg font-bold text-gray-800">
                  £{parseFloat(webHostingCost || 0).toFixed(2)}
                  <span className="text-xs font-normal text-gray-500">/mo</span>
                </span>
              </div>
              <div className="flex flex-col bg-blue-50 rounded-lg px-4 py-3 border border-blue-200">
                <span className="text-xs text-gray-500 mb-1">New Digital Services Cost</span>
                <span className="text-lg font-bold text-blue-700">
                  £{totalNewDigitalCost.toFixed(2)}
                  <span className="text-xs font-normal text-blue-500">/mo</span>
                </span>
                <span className="text-xs text-gray-400 mt-0.5">
                  {licenceMonthlyCost > 0 && `Licences: £${licenceMonthlyCost.toFixed(2)}`}
                  {licenceMonthlyCost > 0 && selectedAxo?.price > 0 && ' + '}
                  {selectedAxo?.price > 0 && `AXO: £${selectedAxo.price.toFixed(2)}`}
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── CURRENT MOBILE COSTS ── */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-200">
        <div
          className="bg-gradient-to-r from-blue-800 via-blue-900 to-black text-white p-6 cursor-pointer flex justify-between items-center hover:from-gray-700 hover:via-gray-800 hover:to-gray-900 transition-all duration-300"
          onClick={() => setisCurrentMobileCostsOpen(v => !v)}
        >
          <div className="flex items-center space-x-3">
            <PoundSterlingIcon className="w-6 h-6" />
            <h2 className="text-xl font-bold tracking-wide">CURRENT MOBILE COSTS</h2>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-300 text-sm font-medium">{isCurrentMobileCostsOpen ? 'Collapse' : 'Expand'}</span>
            <div className="bg-gray-700 rounded-full p-1">
              {isCurrentMobileCostsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </div>
        <div className={isCurrentMobileCostsOpen ? 'block' : 'hidden'}>
          <CurrentMobileCosts
            selectedConnectionId={selectedConnectionId}
            charges={localServiceCharges}
            totalMonthlyCost={totalMonthlyCost}
            isHostedLifetimeLicenses={isHostedLifetimeLicenses}
            leasedLines={leasedLines}
            onMobileDataUpdate={setMobileData}
          />
        </div>
      </div>

      {/* ── COST COMPARISON ── */}
      <CostComparisonComponent
        selectedConnectionId={selectedConnectionId}
        charges={localServiceCharges}
        totalMonthlyCost={totalMonthlyCost}
        itServicesTotal={itServicesTotal}
        isHostedLifetimeLicenses={isHostedLifetimeLicenses}
        leasedLines={leasedLines}
        totalEquipmentCost={totalEquipmentCost}
        mobileData={mobileData}
        onLeaseDataUpdate={onLeaseDataUpdate}
        leaseCalculatorData={leaseCalculatorData}
        commission={commission}
        hardwareItems={hardwareItems}
        additionalHardware={additionalHardware}
        currentLines={currentLines}
        dataBroadband={dataBroadband}
        lineRequirements={lineRequirements}
        telecomServices={telecomServices}
        adminUpdate={adminUpdate}
        totalNewDigitalCost={totalNewDigitalCost}
        webHostingCost={webHostingCost}
      />
    </div>
  );
};

export default CallCostsComponent;